import { User } from ".";
import Group from "./Group";
import Model from "./Model";

export class DatabaseUpdatePayload {
  id!: string;
  active?: string;
  name?: string;
  description?: string;
  color?: string;
  provider?: string;
  notificationsEnabled?: boolean;
  statsEnabled?: boolean;
  qrSignupEnabled?: boolean;
  patientMessagingEnabled?: boolean;
  customIdRequired?: boolean;
  monitorStartsAt?: string | null;
  monitorFinishesAt?: string | null;
  monitorFrequency?: number | null;
}

export default class Database extends Model {
  static type = "databases";
  static showIncludes = "groups.questions,*";
  static counters = { databases: "doctorsCount,questionsCount,groupsCount" };

  name: string;
  description: string;
  active: boolean;
  color?: string;
  provider?: string;
  imageUrl?: string;
  createdAt: Date;
  uuid: string;
  qrSignupEnabled: boolean;
  // A new survey asks for its custom id (the "nota") and cannot be
  // created without it.
  customIdRequired: boolean;
  surveysCount: number;
  doctorsCount: number;
  groupsCount: number;
  questionsCount: number;
  groups: Group[];
  user: User;
  doctors: User[];
  statsEnabled?: boolean;
  kind?: "standalone" | "parent" | "child";
  parentId?: number | null;
  parentDatabase?: { id: number; name: string; kind: string } | null;
  childDatabasesCount?: number;
  notificationContent?: string | null;

  /**
   * Patient involvement. `available` is tenant-wide (WhatsApp is
   * configured); `enabled` is this database's opt-in; `active` is both,
   * and is the only flag that may reveal patient UI.
   */
  patientMessagingAvailable?: boolean;
  patientMessagingEnabled?: boolean;
  patientMessagingActive?: boolean;
  // Set when Meta is blocking every message (payment, expired token...).
  patientMessagingAlert?: string | null;

  get isStandaloneKind() {
    return !this.kind || this.kind === "standalone";
  }

  get isParentKind() {
    return this.kind === "parent";
  }

  get isChildKind() {
    return this.kind === "child";
  }

  /**
   * Mirrors Database#structure_editable? on the backend so the UI can
   * gate edit affordances (groups, questions, templates) consistently
   * with the model-level lock.
   *
   *  - standalone: always editable
   *  - parent: editable only until it has produced its first child
   *  - child: always frozen (snapshot from the parent)
   */
  get isStructureEditable(): boolean {
    if (this.isChildKind) return false;
    if (this.isParentKind && (this.childDatabasesCount || 0) > 0) return false;
    return true;
  }

  /**
   * Whether limited, non-destructive CONTENT edits are allowed:
   * question text, renaming option labels, adding options. Permitted on
   * a frozen parent blueprint too (the change propagates to children),
   * never on a child. Mirrors Question#structure_lock_safe_change?.
   */
  get isContentEditable(): boolean {
    return !this.isChildKind;
  }

  // Monitoring fields for notification system
  monitorStartsAt?: string;
  monitorFinishesAt?: string;
  monitorFrequency?: number;
  notificationsEnabled?: boolean;

  static init() {
    const database = new Database();
    database.name = "Database senza titolo";
    return database;
  }
}
